import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import { DEFAULT_LOCALE, isLocale, type Locale } from './i18n';

/**
 * Owns the locale for everything below it (§5, §11).
 *
 * URL is the single source of truth: `/` and `/what-to-see` are English, `/ka` and
 * `/ka/what-to-see` are Georgian. This layout reads the locale off the route and pushes
 * it into i18next and <html lang>, so a deep link in either language renders correctly
 * on first paint and is shareable — a requirement in §7 "Global requirements".
 *
 * An unknown prefix (e.g. `/ru/...`) redirects to the English equivalent rather than
 * 404ing, so a mistyped or legacy locale prefix still lands on real content.
 *
 * Path helpers live in lib/locale.ts.
 */
export function LocaleLayout() {
  const { locale: localeParam } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();

  // `/:locale` is absent on the English branch of the route tree, which is correct —
  // English is unprefixed.
  const locale: Locale = isLocale(localeParam) ? localeParam : DEFAULT_LOCALE;
  const hasUnknownPrefix = localeParam !== undefined && !isLocale(localeParam);

  // i18next is already initialised to the URL's locale before React renders (see
  // i18n.ts), so this effect only handles SUBSEQUENT client-side locale changes — by
  // which point every component is mounted and subscribed to `languageChanged`.
  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  }, [locale, i18n]);

  if (hasUnknownPrefix) {
    const stripped = location.pathname.replace(`/${localeParam}`, '') || '/';
    return <Navigate to={stripped} replace />;
  }

  return <Outlet />;
}
